import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import ErrorBanner from '../../components/shared/ErrorBanner'
import InlineLoading from '../../components/shared/InlineLoading'
import {
    BULK_OPTIONAL_COLUMNS,
    BULK_REQUIRED_COLUMNS,
    isoDateIst,
    parseBulkHolidays,
    timeIst,
} from '../../helpers/holidays'
import HolidayService from '../../services/holidayService'
import { errorMessage } from '../../services/axios/errorMessage'
import { parseCSV } from '../../utils/csv'
import { FONT, SURFACE, TEXT, cardSx, pageSx } from '../../theme/tokens'
import HolidayHeader from './HolidayHeader'

const cellSx = { borderColor: SURFACE.border, typography: 'caption' }

/**
 * Upload many holidays from a CSV (POST /comms/bulk-holiday), as the
 * applause app's "Bulk Holidays" did: the holidays only, not their
 * pipelines. Every row is checked and shown before anything is sent: the
 * rules of a single new holiday, and no id repeated in the file or already
 * a holiday (the API refuses only ids of active holidays and overwrites
 * inactive and completed ones), so the stored ids are loaded first.
 */
const BulkHolidays = () => {
    const navigate = useNavigate()
    const fileInputRef = useRef(null)
    const [fileName, setFileName] = useState('')
    const [csv, setCsv] = useState(null)
    const [existingIds, setExistingIds] = useState(null)
    const [loadError, setLoadError] = useState('')
    const [error, setError] = useState('')
    const [uploading, setUploading] = useState(false)

    useEffect(() => {
        HolidayService.holidayIds()
            .then(setExistingIds)
            .catch((err) =>
                setLoadError(
                    `Existing holidays could not be checked: ${errorMessage(
                        err?.response
                    )}`
                )
            )
    }, [])

    const parsed = useMemo(
        () =>
            csv && existingIds
                ? parseBulkHolidays(csv.headers, csv.rows, { existingIds })
                : null,
        [csv, existingIds]
    )

    const onFile = (e) => {
        const file = e.target.files?.[0]
        if (!file) return
        setFileName(file.name)
        setError('')
        const reader = new FileReader()
        reader.onload = (ev) => setCsv(parseCSV(ev.target.result))
        reader.readAsText(file)
    }

    const onUpload = async () => {
        setUploading(true)
        setError('')
        try {
            await HolidayService.bulkCreate(parsed.holidays)
            navigate('/holidays', {
                state: {
                    holidayNotice: `${parsed.holidays.length} holiday${
                        parsed.holidays.length === 1 ? '' : 's'
                    } uploaded. Each needs a pipeline with its holiday ID as the event type to send comms.`,
                },
            })
        } catch (err) {
            setError(errorMessage(err?.response))
            setUploading(false)
        }
    }

    const ready =
        parsed && parsed.errors.length === 0 && parsed.holidays.length > 0

    return (
        <Box sx={pageSx}>
            <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 3, md: 4 } }}>
                <HolidayHeader
                    title="Bulk upload holidays"
                    subtitle="One holiday per CSV row. Dates and times are IST."
                />
                <Paper elevation={0} sx={{ ...cardSx, p: 3 }}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                            gap: 2,
                            mb: 3,
                        }}
                    >
                        <Box>
                            <Typography variant="overline">
                                Required columns
                            </Typography>
                            {BULK_REQUIRED_COLUMNS.map((c) => (
                                <Typography
                                    key={c}
                                    sx={{
                                        typography: 'caption',
                                        color: TEXT.secondary,
                                        fontFamily: FONT.mono,
                                    }}
                                >
                                    {c}
                                    {c === 'holiday_date' && ' (DD/MM/YYYY)'}
                                    {c === 'comms_time' && ' (HH:MM)'}
                                </Typography>
                            ))}
                        </Box>
                        <Box>
                            <Typography variant="overline">
                                Optional columns
                            </Typography>
                            {BULK_OPTIONAL_COLUMNS.map((c) => (
                                <Typography
                                    key={c}
                                    sx={{
                                        typography: 'caption',
                                        color: TEXT.secondary,
                                        fontFamily: FONT.mono,
                                    }}
                                >
                                    {c}
                                    {c === 'allowed_countries' &&
                                        ' (codes, e.g. "US, AE")'}
                                </Typography>
                            ))}
                        </Box>
                    </Box>

                    <Button
                        variant="outlined"
                        startIcon={<UploadFileIcon />}
                        onClick={() => fileInputRef.current?.click()}
                        disabled={existingIds === null}
                    >
                        {fileName || 'Choose a CSV file'}
                    </Button>
                    {existingIds === null && !loadError && (
                        <InlineLoading
                            label="Checking existing holidays…"
                            justify="flex-start"
                            py={1.5}
                        />
                    )}
                    {loadError && (
                        <Box sx={{ mt: 2 }}>
                            <ErrorBanner message={loadError} />
                        </Box>
                    )}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        hidden
                        aria-label="Holidays CSV file"
                        onChange={onFile}
                    />

                    {parsed?.errors.length > 0 && (
                        <Box sx={{ mt: 2, display: 'grid', gap: 1 }}>
                            {parsed.errors.map((msg) => (
                                <ErrorBanner key={msg} message={msg} />
                            ))}
                        </Box>
                    )}

                    {parsed?.holidays.length > 0 && (
                        <TableContainer sx={{ mt: 3 }}>
                            <Table size="small" sx={{ minWidth: 760 }}>
                                <TableHead>
                                    <TableRow>
                                        {[
                                            'Holiday ID',
                                            'Holiday',
                                            'Date',
                                            'Comms time',
                                            'Comms days before',
                                            'Reminder days before',
                                            'Countries',
                                        ].map((h) => (
                                            <TableCell key={h} sx={cellSx}>
                                                <Typography variant="overline">
                                                    {h}
                                                </Typography>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {parsed.holidays.map((h) => (
                                        <TableRow
                                            key={h.holiday_id}
                                            data-testid="bulk-row"
                                        >
                                            <TableCell
                                                sx={{
                                                    ...cellSx,
                                                    color: TEXT.primary,
                                                    fontFamily: FONT.mono,
                                                }}
                                            >
                                                {h.holiday_id}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    ...cellSx,
                                                    color: TEXT.primary,
                                                }}
                                            >
                                                {h.holiday_name}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    ...cellSx,
                                                    color: TEXT.secondary,
                                                }}
                                            >
                                                {isoDateIst(h.holiday_date)}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    ...cellSx,
                                                    color: TEXT.secondary,
                                                }}
                                            >
                                                {timeIst(
                                                    h.events[0].event_time
                                                )}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    ...cellSx,
                                                    color: TEXT.secondary,
                                                }}
                                            >
                                                {
                                                    h.events[0]
                                                        .days_before_holiday
                                                }
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    ...cellSx,
                                                    color: TEXT.secondary,
                                                }}
                                            >
                                                {h.events[1]
                                                    ? h.events[1]
                                                          .days_before_holiday
                                                    : '-'}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    ...cellSx,
                                                    color: TEXT.secondary,
                                                }}
                                            >
                                                {h.allowed_countries.length
                                                    ? h.allowed_countries.join(
                                                          ', '
                                                      )
                                                    : 'All'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {error && (
                        <Box sx={{ mt: 2 }}>
                            <ErrorBanner message={error} />
                        </Box>
                    )}

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 1.5,
                            mt: 3,
                        }}
                    >
                        <Button
                            variant="outlined"
                            onClick={() => navigate('/holidays')}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={onUpload}
                            disabled={!ready || uploading}
                        >
                            {uploading ? (
                                <CircularProgress size={16} />
                            ) : ready ? (
                                `Upload ${parsed.holidays.length} holiday${
                                    parsed.holidays.length === 1 ? '' : 's'
                                }`
                            ) : (
                                'Upload holidays'
                            )}
                        </Button>
                    </Box>
                </Paper>
            </Box>
        </Box>
    )
}

export default BulkHolidays
